import { type CSSProperties, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollView, Stack, Text, XStack, YStack } from "tamagui";
import KioskIcon, { KioskIconName } from "./KioskIcon";
import { KioskBackBtn, KioskCTA } from "./components/KioskParts";
import "./kiosk.scss";
import { fireConfetti } from "./kioskFx";
import { kiosk } from "./kioskTheme";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import ReceiptPaper from "@components/layout/ReceiptPaper";

/* 與瓦斯訂購的下單成功頁同一隻角色、同一組火花方向。
   兩條流程的終點放同一個慶祝畫面，客戶不會覺得走進了另一個 App。 */
const mascotModule = require("@styles/images/gas-mascot-hero.png");
const MascotImg = (mascotModule?.default ?? mascotModule) as string;

/** 火花往斜上與兩側彈開；不往正下方——那邊接著訂單摘要白卡。 */
const SPARKS = [
  { "--sx": "-30px", "--sy": "-26px", "--sr": "-28deg" },
  { "--sx": "30px", "--sy": "-26px", "--sr": "28deg" },
  { "--sx": "-40px", "--sy": "2px", "--sr": "-72deg" },
  { "--sx": "40px", "--sy": "2px", "--sr": "72deg" },
] as unknown as CSSProperties[];
import { useKiosk } from "./useKiosk";
import { useOrder } from "@utils/store/cart/useOrder";
import { track, clearEntrySource } from "@lib/tracker";

/**
 * 下單成功獨立頁（/kiosk/success），與 kiosk 流程一致的視覺：
 * 藍色漸層 hero（綠勾彈出 + 彩帶）＋ 上浮白卡訂單摘要 ＋ 漸層 CTA。
 * 成功資訊存在 useKiosk.lastSuccess（sessionStorage），重新整理不消失。
 */
const KioskSuccess = () => {
  const navigate = useNavigate();
  const { lastSuccess, setLastSuccess, hidePrice } = useKiosk();
  // 瓦斯行開啟隱藏金額時，成功頁不顯示應付金額
  const { hideAmountOnPending } = useOrder();
  const heroRef = useRef<HTMLDivElement | null>(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!lastSuccess) {
      navigate("/kiosk", { replace: true });
      return;
    }
    if (heroRef.current) fireConfetti(heroRef.current);
    if (navigator.vibrate) navigator.vibrate([20, 50, 20, 50, 30]);
    if (!trackedRef.current) {
      trackedRef.current = true;
      track("order_success_view", { client_request_id: lastSuccess.clientRequestId });
      clearEntrySource(); // 下單成功，離開漏斗，清掉 entry_source 附掛狀態
    }
  }, [lastSuccess, navigate]);

  if (!lastSuccess) return null;

  const leave = (to: string) => {
    setLastSuccess(null); // 離開後清掉，避免再次進入看到舊結果
    navigate(to, { replace: true });
  };

  return (
        // 底色沿用全站的淡霧（.gas-skin-v2-bg，定義在 all.scss）而不是複製一份：
    // 兩份定義遲早會走鐘，而客戶在同一個 App 裡會前後看到這兩條流程。
    <YStack flex={1} position="relative" className="gas-skin-v2-bg">
      <ScrollView flex={1} className="kiosk-noscroll kiosk-stagger" contentContainerStyle={{ paddingBottom: 24 }}>
        {/* 藍色漸層 hero */}
        {/* 慶祝區。原本是藍色漸層帶配白底綠勾徽章——底色換成淡霧後那條帶子是整頁唯一的重色，
            而且綠勾是「系統確認」的語彙；角色是「有人替你高興」的語彙，成交畫面該用後者。 */}
        {/* 角色縮小、與標題橫排：這一頁的重點是中間的收據（金額、編號、時段、地址、付款方式），
            原本直排的大角色（128px）＋ 上下留白吃掉近三分之一個畫面，收據只露出前三行。 */}
        <XStack ref={heroRef as any} position="relative" ai="center" jc="center" gap={14} pt={18} pb={6} px={20}>
          <Stack position="relative" ai="center" jc="center" flexShrink={0}>
            {SPARKS.map((sp, i) => (
              <span key={i} aria-hidden className="gas-done-spark" style={{ ...sp, animationDelay: `${0.12 + i * 0.04}s` } as CSSProperties} />
            ))}
            <img
              src={MascotImg}
              alt=""
              className="kiosk-ringpop"
              style={{ width: 68, height: 68, objectFit: "contain", display: "block", position: "relative" }}
            />
          </Stack>
          <YStack flexShrink={1}>
            <Text color={v2.hazeInk} fontSize={21} fontWeight="900" letterSpacing={-0.5}>
              下單成功！
            </Text>
            <Text color={v2.hazeSub} fontSize={12.5} mt={3} fontWeight="500">
              已收到您的訂單，正在為您安排配送
            </Text>
          </YStack>
        </XStack>

        {/* 訂單摘要做成「剛印出來的收據」（與瓦斯訂購的下單成功頁共用 ReceiptPaper）：
            出紙口咬住紙的上緣、紙從上方印出來、下緣是撕齒。
            原本是一張上浮 40px 的白卡——那個負外距是為了壓在藍色 hero 上，
            hero 換成淡霧後沒有東西可壓，收據自己帶 18px 上外距就夠。 */}
        <ReceiptPaper
          // 隱藏金額的瓦斯行不揭露金額；營業客戶（hidePrice）改顯示「專員報價」
          amount={!hideAmountOnPending && !hidePrice ? lastSuccess.total : null}
          fallbackTitle={hidePrice ? "專員報價" : lastSuccess.orderNo}
          rows={[
            { k: "訂單編號", v: lastSuccess.orderNo },
            { k: "商品", v: `共 ${lastSuccess.itemCount} 件` },
            { k: "配送時段", v: lastSuccess.timeText },
            { k: "送達地址", v: lastSuccess.address },
            { k: "付款方式", v: lastSuccess.paymentText },
          ]}
        />

        <YStack px={16} mt={4}>
          {/* 溫馨提示 */}
          <XStack
            ai="center"
            gap={9}
            mt={14}
            px={14}
            py={12}
            borderRadius={14}
            backgroundColor={kiosk.pillBg}>
            <KioskIcon name="phone" size={16} color={kiosk.blue} />
            <Text flex={1} fontSize={12.5} color={kiosk.blueD} fontWeight="600" lineHeight={18}>
              配送前外送員會以電話與您聯繫，請保持電話暢通
            </Text>
          </XStack>
        </YStack>
      </ScrollView>

      {/* 底部 CTA */}
      {/* 兩顆按鈕並排（原本上下疊，底欄就佔掉約 150px），省下的高度讓給收據 */}
      <XStack backgroundColor="#fff" borderTopWidth={1} borderColor={kiosk.line} px={16} pt={10} gap={10} style={{ flex: "0 0 auto", paddingBottom: "calc(10px + env(safe-area-inset-bottom, 0px))" }}>
        {/* 「返回首頁」要回 App 首頁 /home，不是 kiosk 自己的首頁。
            kiosk 最初是獨立的一鍵下單展示流程，那時「首頁」指的就是 kiosk；
            但 QR 註冊完的客戶下單後會停在這裡，按「返回首頁」卻又回到 kiosk，
            看起來像沒有反應。要再逛商品有旁邊的「查看訂單」與 kiosk 入口可用。 */}
        <YStack flex={1}>
          <KioskBackBtn label="返回首頁" onPress={() => leave("/home")} />
        </YStack>
        <YStack flex={1.4}>
          <KioskCTA label="查看訂單" onPress={() => leave(lastSuccess.orderId ? `/orders/${lastSuccess.orderId}` : "/kiosk")} />
        </YStack>
      </XStack>
    </YStack>
  );
};

/** 摘要列：icon + 標籤 + 值。 */
const InfoRow = ({ icon, k, v }: { icon: KioskIconName; k: string; v: string }) => (
  <XStack ai="center" gap={11} py={9}>
    <Stack width={30} height={30} borderRadius={9} backgroundColor={kiosk.pillBg} ai="center" jc="center">
      <KioskIcon name={icon} size={16} color={kiosk.blue} />
    </Stack>
    <Text fontSize={12.5} color={kiosk.grey} fontWeight="600" minWidth={62}>
      {k}
    </Text>
    <Text flex={1} fontSize={13.5} color={kiosk.ink} fontWeight="700" textAlign="right" lineHeight={19}>
      {v}
    </Text>
  </XStack>
);

export default KioskSuccess;
